import posthog from 'posthog-js';
import { PostHogProvider as BasePostHogProvider, useFeatureFlagEnabled } from 'posthog-js/react';
import { ReactNode } from 'react';
import { PosthogFeatureFlag } from '../../types/posthog-feature-flag.enum';
import { config } from '../../utils/config';

const POSTHOG_KEY = config.env.VITE_PUBLIC_POSTHOG_KEY;
const POSTHOG_HOST = config.env.VITE_PUBLIC_POSTHOG_HOST;

if (!POSTHOG_KEY) {
  throw new Error('Add your Posthog Key to the .env file');
}

if (!POSTHOG_HOST) {
  throw new Error('Add your Posthog Host to the .env file');
}

export const posthogClient = posthog.init(config.env.VITE_PUBLIC_POSTHOG_KEY, {
  api_host: config.env.VITE_PUBLIC_POSTHOG_HOST,
  defaults: '2025-05-24',
  persistence: 'localStorage',
  autocapture: false,
  capture_pageview: false,
  capture_pageleave: false,
  disable_session_recording: true,
  disable_scroll_properties: true,
  disable_web_experiments: true,
  disable_surveys: true,
  disable_compression: false,
  session_recording: {
    recordCrossOriginIframes: false,
  },
  loaded: posthog => {
    posthog.setPersonPropertiesForFlags({}, false);
  },
});

type PosthogProviderProps = {
  children: ReactNode;
};

export const PosthogProvider = ({ children }: PosthogProviderProps) => {
  return <BasePostHogProvider client={posthogClient}>{children}</BasePostHogProvider>;
};

/** Flags read as off until posthog says otherwise, and re-render when it does. */
export function useFeatureFlag(flag: PosthogFeatureFlag): boolean {
  return useFeatureFlagEnabled(flag, false);
}

export const useIsOoCaseNewSectionsEnabled = () => {
  return useFeatureFlag(PosthogFeatureFlag.OO_CASE_NEW_SECTIONS);
};

export const useIsOoCaseSolicitorsEnabled = () => {
  return useFeatureFlag(PosthogFeatureFlag.OO_CASE_SOLICITORS);
};

export const useIsOoCaseDipAcceptedEditEnabled = () => {
  return useFeatureFlag(PosthogFeatureFlag.OO_CASE_DIP_ACCEPTED_EDIT);
};

export const useIsOoCaseCreateFlowEnabled = () => {
  return useFeatureFlag(PosthogFeatureFlag.OO_CASE_CREATE_FLOW);
};

export const useIsOoCaseSubmissionEnabled = () => {
  return useFeatureFlag(PosthogFeatureFlag.OO_CASE_SUBMISSION);
};

export const useIsHppPermissionedPagesEnabled = () => {
  return useFeatureFlag(PosthogFeatureFlag.HPP_PERMISSIONED_PAGES);
};

export const useIsTypesenseCaseSearchEnabled = () => {
  return useFeatureFlag(PosthogFeatureFlag.USE_TYPESENSE_CASE_SEARCH);
};

export const useIsResidentialStatusEnabled = () => {
  return useFeatureFlag(PosthogFeatureFlag.RESIDENTIAL_STATUS_ENABLED);
};

export const useIsStrideDesignSystemEnabled = () => {
  return useFeatureFlag(PosthogFeatureFlag.STRIDE_DESIGN_SYSTEM);
};
